"use client"

import React, { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Upload,
  FileText,
  ImageIcon,
  File,
  Loader2,
  CheckCircle,
  Brain,
  ArrowRight,
  Layout,
  Download,
  Eye,
  Sparkles,
  AlertTriangle,
  Settings,
  BookOpen,
  FileDown,
  Printer,
  RefreshCw,
  Edit3,
} from "lucide-react"

// API Client
const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:4000';

const apiClient = {
  post: async (endpoint: string, data: any) => {
    const url = `${BACKEND_URL}${endpoint}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }
};

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "text/plain": [".txt"],
}

interface CompactStudyConfig {
  layout: 'compact' | 'standard';
  columns: 1 | 2 | 3;
  equations: 'all' | 'key' | 'minimal';
  examples: 'full' | 'summary' | 'references';
  answers: 'inline' | 'appendix' | 'separate';
  fontSize: string;
  margins: 'narrow' | 'normal' | 'wide';
  outputFormat: 'html' | 'pdf' | 'markdown' | 'all';
  paperSize?: 'a4' | 'letter' | 'legal';
  orientation?: 'portrait' | 'landscape';
  title?: string;
}

interface CompactStudyResponse {
  success: boolean;
  message: string;
  html?: string;
  pdf?: string;
  markdown?: string;
  metadata: {
    generatedAt: string;
    format: string;
    sourceFiles: string[];
    stats: {
      totalSections: number;
      totalFormulas: number;
      totalExamples: number;
      estimatedPrintPages: number;
      totalImages?: number;
    };
    preservationScore: number;
  };
  warnings?: string[];
  errors?: string[];
}

export function CompactStudyGenerator() {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [results, setResults] = useState<CompactStudyResponse | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [showConfiguration, setShowConfiguration] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])
  const [processingErrors, setProcessingErrors] = useState<any[]>([])

  const [studyConfig, setStudyConfig] = useState<CompactStudyConfig>({
    layout: 'compact',
    columns: 2,
    equations: 'key',
    examples: 'summary',
    answers: 'inline',
    fontSize: '11pt',
    margins: 'narrow',
    outputFormat: 'html',
    paperSize: 'a4',
    orientation: 'portrait',
    title: ''
  })

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setUploadedFiles(prev => [...prev, ...files])
  }, [])

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const proceedToConfiguration = () => {
    setShowConfiguration(true)
  }

  const generateCompactStudy = async () => {
    if (uploadedFiles.length === 0) return

    setIsProcessing(true)
    setProcessingErrors([])
    setWarnings([])

    try {
      // Convert files to base64
      const filesData = await Promise.all(
        uploadedFiles.map(async (file) => {
          const buffer = await file.arrayBuffer()
          const base64 = Buffer.from(buffer).toString('base64')
          
          return {
            name: file.name,
            type: 'general' as const,
            content: base64
          }
        })
      )

      const requestData = {
        files: filesData,
        config: studyConfig,
      }

      const data: CompactStudyResponse = await apiClient.post("/api/generate-compact-study", requestData)
      
      setResults(data)
      setShowResults(true)
      
      if (data.warnings && data.warnings.length > 0) {
        setWarnings(data.warnings)
      }

    } catch (error) {
      console.error("Error generating compact study guide:", error)
      setProcessingErrors([{
        type: 'generation',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        recoverable: true,
        suggestions: ['Try uploading fewer files', 'Check file formats are supported']
      }])
    } finally {
      setIsProcessing(false)
    }
  }

  const downloadHTML = () => {
    if (results?.html) {
      const blob = new Blob([results.html], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "compact-study-guide.html"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Results Display */}
          {showResults && results && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-6 w-6 text-green-600" />
                  <h2 className="font-serif text-2xl font-bold text-green-800">Compact Study Guide Generated!</h2>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-2">
                    <h3 className="font-semibold text-green-800">Content Statistics</h3>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>📚 {results.metadata.stats.totalSections} sections organized</p>
                      <p>🧮 {results.metadata.stats.totalFormulas} formulas preserved</p>
                      <p>💡 {results.metadata.stats.totalExamples} worked examples included</p>
                      <p>📄 {results.metadata.stats.estimatedPrintPages} estimated print pages</p>
                      <p>✅ {Math.round(results.metadata.preservationScore)}% content preservation</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-semibold text-green-800">Actions</h3>
                    <div className="flex flex-wrap gap-2">
                      {results.html && (
                        <Button onClick={downloadHTML} className="gap-2 bg-green-600 hover:bg-green-700">
                          <Download className="h-4 w-4" />
                          Download HTML
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Preview */}
                {results.html && (
                  <div className="border border-green-300 rounded-lg overflow-hidden">
                    <div className="bg-green-100 px-4 py-2 border-b border-green-300">
                      <h4 className="font-semibold text-green-800 flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Preview
                      </h4>
                    </div>
                    <div className="bg-white">
                      <iframe
                        srcDoc={results.html}
                        className="w-full h-[600px] border-0"
                        title="Compact Study Guide Preview"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Configuration Panel */}
          {showConfiguration && !showResults && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="h-6 w-6 text-blue-600" />
                  <h2 className="font-serif text-2xl font-bold text-blue-800">Configuration</h2>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="layout">Layout Style</Label>
                      <Select value={studyConfig.layout} onValueChange={(value: 'compact' | 'standard') => 
                        setStudyConfig(prev => ({ ...prev, layout: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="compact">Compact</SelectItem>
                          <SelectItem value="standard">Standard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="columns">Columns</Label>
                      <Select value={studyConfig.columns.toString()} onValueChange={(value) => 
                        setStudyConfig(prev => ({ ...prev, columns: parseInt(value) as 1 | 2 | 3 }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Column</SelectItem>
                          <SelectItem value="2">2 Columns</SelectItem>
                          <SelectItem value="3">3 Columns</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-blue-200 flex justify-center">
                  <Button 
                    onClick={generateCompactStudy} 
                    disabled={isProcessing}
                    className="gap-2 bg-blue-600 hover:bg-blue-700 px-8 py-3"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Brain className="h-5 w-5" />
                        Generate Study Guide
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* File Upload Section */}
          {!showConfiguration && !showResults && (
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-gray-400 transition-colors">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-700">Upload your study materials</p>
                      <p className="text-sm text-gray-500">PDF, DOCX, or TXT files</p>
                    </div>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept=".pdf,.docx,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold">Uploaded Files:</h3>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">{file.name}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeFile(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    
                    <Button 
                      onClick={proceedToConfiguration}
                      className="mt-4"
                    >
                      Configure & Generate
                    </Button>
                  </div>
                )}
              </div>

              {/* Warnings */}
              {warnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <h3 className="font-semibold text-yellow-800">Warnings</h3>
                  </div>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {warnings.map((warning, index) => (
                      <li key={index}>• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Errors */}
              {processingErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <h3 className="font-semibold text-red-800">Processing Errors</h3>
                  </div>
                  {processingErrors.map((error, index) => (
                    <div key={index} className="text-sm text-red-700 space-y-1">
                      <p><strong>{error.type}:</strong> {error.message}</p>
                      {error.suggestions && (
                        <div>
                          <p className="font-medium">Suggestions:</p>
                          <ul className="ml-4">
                            {error.suggestions.map((suggestion: string, i: number) => (
                              <li key={i}>• {suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}